import React from 'react';
import type { SketchControlKind } from '@api/appBuilder';
import InkGradient from './InkGradient';
import type { Box, SketchLayout } from './sketchLayout';
import { knob, pill, roundedRect } from './sketchPaths';
import { useInk } from './useInk';
import { artPlacement, VIEWER_ART } from './viewerArt';
import { Drawing, Ink, Label, Soft, SoftFill } from './BuildSketch.styled';

interface SketchDrawingProps {
  readonly width: number;
  readonly height: number;
  readonly layout: SketchLayout;
  /** How many parts are drawn: the viewers first, then each control, then the export button. */
  readonly revealed: number;
  readonly headingLabel: string;
  readonly exportLabel: string;
}

const controlPaths = (kind: SketchControlKind, box: Box): string[] => {
  const y = box.y + 24;
  switch (kind) {
    case 'slider':
      return [`M${box.x} ${y + 14} H${box.x + box.w}`, knob(box.x + box.w * 0.4, y + 14, 8)];
    case 'select':
      return [pill({ x: box.x, y, w: box.w, h: 26 }), `M${box.x + box.w - 24} ${y + 10} l6 6 l6 -6`];
    case 'chips': {
      const w = Math.min(84, (box.w - 16) / 3);
      return [0, 1, 2].map((i) => pill({ x: box.x + i * (w + 8), y, w, h: 24 }));
    }
    case 'toggle':
      return [pill({ x: box.x, y, w: 42, h: 24 }), knob(box.x + 30, y + 12, 7)];
    default:
      return [pill({ x: box.x, y, w: box.w, h: 28 })];
  }
};

const SketchDrawing: React.FC<SketchDrawingProps> = ({ width, height, layout, revealed, headingLabel, exportLabel }) => {
  const { id, ink } = useInk();
  const { frame, viewers, heading, controls, exportPill } = layout;

  return (
    <Drawing width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" data-testid="app-builder__sketch-drawing">
      <InkGradient id={id} width={width} height={height} />
      <Soft d={roundedRect(frame)} />
      {revealed > 0 && (
        <g data-part="viewers">
          {viewers.map(({ kind, box }) => {
            const { transform, scale } = artPlacement(box);
            return (
              <g key={`${kind}-${box.x}-${box.y}`}>
                <SoftFill x={box.x} y={box.y} width={box.w} height={box.h} rx={14} />
                <Ink d={roundedRect(box)} stroke={ink} pathLength={1} />
                <g transform={transform}>
                  {VIEWER_ART[kind].map((d) => (
                    <Ink key={d} d={d} stroke={ink} strokeWidth={2 / scale} pathLength={1} />
                  ))}
                </g>
              </g>
            );
          })}
          <Label x={heading.x} y={heading.y + 16} $strong>
            {headingLabel}
          </Label>
        </g>
      )}
      {controls.slice(0, Math.max(0, revealed - 1)).map(({ kind, label, box }) => (
        <g key={`${label}-${box.y}`} data-part="control">
          <Label x={box.x} y={box.y + 16}>
            {label}
          </Label>
          {controlPaths(kind, box).map((d) => (
            <Ink key={d} d={d} stroke={ink} pathLength={1} />
          ))}
        </g>
      ))}
      {revealed > controls.length + 1 && (
        <g data-part="export">
          <Ink d={pill(exportPill)} stroke={ink} pathLength={1} />
          <Label x={exportPill.x + exportPill.w / 2} y={exportPill.y + exportPill.h / 2 + 4} textAnchor="middle">
            {exportLabel}
          </Label>
        </g>
      )}
    </Drawing>
  );
};

export default SketchDrawing;
